/**
 * Tree Helper Utilities
 *
 * Common tree operations used across all picker components
 */

import type { BaseTreeNode } from './types';

/**
 * Formats field name from camelCase to Title Case
 *
 * @example
 * formatFieldName('requiredInfo') // → 'Required Info'
 * formatFieldName('goalToBeDone') // → 'Goal To Be Done'
 */
export function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Finds a node by key in the tree (recursive)
 *
 * @param key - Node key to find
 * @param nodes - Array of tree nodes to search
 * @returns Found node or null
 */
export function findNodeByKey<T extends BaseTreeNode>(
  key: string,
  nodes: T[]
): T | null {
  for (const node of nodes) {
    if (node.key === key) {
      return node;
    }
    if (node.children) {
      const found = findNodeByKey(key, node.children as T[]);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Collects all checkable keys from a node and its descendants (recursive)
 *
 * @param node - Tree node to collect keys from
 * @returns Array of checkable keys
 */
export function collectAllCheckableKeys<T extends BaseTreeNode>(node: T): string[] {
  const keys: string[] = [];

  // Add current node if checkable
  if (node.checkable !== false) {
    keys.push(node.key as string);
  }

  // Recursively collect from children
  if (node.children) {
    for (const child of node.children) {
      keys.push(...collectAllCheckableKeys(child as T));
    }
  }

  return keys;
}

/**
 * Checks if all checkable children of a node are selected
 *
 * @param node - Tree node to check
 * @param checkedSet - Set of checked keys
 * @returns True if all checkable children are checked
 */
export function isFullyChecked<T extends BaseTreeNode>(
  node: T,
  checkedSet: Set<string>
): boolean {
  const allKeys = collectAllCheckableKeys(node);
  return allKeys.length > 0 && allKeys.every((key) => checkedSet.has(key));
}

/**
 * Marks a node and all its children as processed
 *
 * @param node - Tree node to mark
 * @param processedSet - Set to add processed keys to
 */
export function markAsProcessed<T extends BaseTreeNode>(
  node: T,
  processedSet: Set<string>
): void {
  const allKeys = collectAllCheckableKeys(node);
  allKeys.forEach((key) => processedSet.add(key));
}
